import { API_BASE_URL } from "../../../config/env";
import { apiFetch } from "../api";
import {
    GenerateRecommendationProductsPayload,
    GenerateRecommendationProductsRankings,
    ICommitmentsResponse,
} from "./types";

/** Locked markets: send empty query so backend does not use them. */
export const LOCKED_MARKET_KEYS = new Set(["RENDA VARIAVEL"]);

/** Map: backend query key -> [frontend market key, backend ranking_text key]. */
const MARKET_QUERY_TO_RANKING_TEXT: Array<{
    queryKey: keyof GenerateRecommendationProductsRankings;
    marketKey: string;
    rankingTextKey: keyof GenerateRecommendationProductsRankings;
}> = [
    { queryKey: "fund_products_query", marketKey: "FUNDOS", rankingTextKey: "fund_products_ranking_text" },
    { queryKey: "retirement_products_query", marketKey: "PREVIDENCIA", rankingTextKey: "retirement_products_ranking_text" },
    { queryKey: "variable_income_products_query", marketKey: "RENDA VARIAVEL", rankingTextKey: "variable_income_products_ranking_text" },
    { queryKey: "fixed_income_products_query", marketKey: "RENDA FIXA", rankingTextKey: "fixed_income_products_ranking_text" },
    { queryKey: "crypto_products_query", marketKey: "CRIPTOATIVOS", rankingTextKey: "crypto_products_ranking_text" },
    { queryKey: "coe_products_query", marketKey: "COE", rankingTextKey: "coe_products_ranking_text" },
];

/**
 * Merges optional ranking_text keys into base rankings for markets that have
 * a non-empty query and a confirmed ranking explanation. Send only for markets
 * that have products and a criterion.
 */
export function mergeRankingTexts(
    baseRankings: GenerateRecommendationProductsRankings,
    confirmedRankingByMarket: Record<string, { ranking_text: string }>
): GenerateRecommendationProductsRankings {
    const result: GenerateRecommendationProductsRankings = { ...baseRankings };
    for (const { queryKey, marketKey, rankingTextKey } of MARKET_QUERY_TO_RANKING_TEXT) {
        if (LOCKED_MARKET_KEYS.has(marketKey)) {
            (result as any)[queryKey] = "";
        } else {
            const query = baseRankings[queryKey];
            const text = confirmedRankingByMarket[marketKey]?.ranking_text?.trim();
            if (typeof query === "string" && query.length > 0 && text) {
                (result as any)[rankingTextKey] = text;
            }
        }
    }
    return result;
}

const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}${month}${day}`;
};

export const fetchCommitments = async (
    beginDate: Date,
    endDate: Date
): Promise<ICommitmentsResponse> => {
    const begin_date = formatDate(beginDate);
    const end_date = formatDate(endDate);

    const params = new URLSearchParams({ begin_date, end_date });


    const data = await apiFetch(
        `/commitments/list_commitments?${params.toString()}`
    )
    return data as ICommitmentsResponse;
};

export const updateCommitmentStatus = async (
    commitmentId: string | number,
    accountId: string | number,
    date: string
): Promise<any> => {
    // Validação dos parâmetros
    if (!commitmentId || commitmentId === "") {
        throw new Error("commitmentId é obrigatório");
    }
    
    // Valida accountId: não pode ser null, undefined ou string vazia (mas pode ser 0)
    if (accountId === null || accountId === undefined || accountId === "") {
        throw new Error("accountId é obrigatório e não pode ser null, undefined ou vazio");
    }
    
    if (!date){// || date === "" || !/^\d{8}$/.test(date)) {
        throw new Error("date é obrigatório e deve estar no formato YYYYmmdd");
    }

    // Cria os query params
    const params = new URLSearchParams({
        account_id: String(accountId),
        date: date,
    });

    const data = await apiFetch(
        `/commitments/update_commitment_status/${commitmentId}?${params.toString()}`,
        {
            method: "PATCH",
        }
    );
    return data;
};

export const generateRecommendationProducts = async (
    payload: GenerateRecommendationProductsPayload
): Promise<any> => {
    const data = await apiFetch(
        "/commitments/generate_recommendation_products",
        {
            method: "POST",
            body: JSON.stringify(payload),
        }
    );
    return data;
};

